// Section 5: Geometry & Measures — pages 98-137
// Grade9Ready — GCSE Maths Topic Bank
// Improved and corrected version: Geometry & Measures

const PAGE_IMAGES = {
  p100_101: "https://customer-assets.emergentagent.com/job_pathway-to-9/artifacts/vx5y2okq_Page100-101.png",
  p104_105: "https://customer-assets.emergentagent.com/job_pathway-to-9/artifacts/iksg7tz2_Page104-105.png",
  p106_107: "https://customer-assets.emergentagent.com/job_pathway-to-9/artifacts/3u3q60z8_Page106-107.png",
  p110_111: "https://customer-assets.emergentagent.com/job_pathway-to-9/artifacts/dv6gc6k6_Page110-111.png",
  p112_113: "https://customer-assets.emergentagent.com/job_pathway-to-9/artifacts/h4gn24g4_Page112-113.png",
  p116_117: "https://customer-assets.emergentagent.com/job_pathway-to-9/artifacts/0ecdrm47_Page116-117.png",
  p118_119: "https://customer-assets.emergentagent.com/job_pathway-to-9/artifacts/jfdizw2u_Page118-119.png",
  p122_123: "https://customer-assets.emergentagent.com/job_pathway-to-9/artifacts/euf4fm43_Page122-123.png",
  p124_125: "https://customer-assets.emergentagent.com/job_pathway-to-9/artifacts/ing7cg17_Page124-125.png",
  p126_127: "https://customer-assets.emergentagent.com/job_pathway-to-9/artifacts/5pneycrl_Page126-127.png",
  p130_131: "https://customer-assets.emergentagent.com/job_pathway-to-9/artifacts/ahh0mmf8_Page130-131.png",
  p132_133: "https://customer-assets.emergentagent.com/job_pathway-to-9/artifacts/n7qt0uzv_Page132-133.png",
};

export const geometryTopics = [
  {
    id: "5.1",
    title: "Symmetry",
    slug: "symmetry",
    pageRef: "Pages 99-101",
    difficulty: "foundation",
    learn: {
      explanation:
        "Line symmetry means a shape can be folded along a mirror line so both halves match exactly. Rotational symmetry means a shape looks the same after a turn of less than 360°.",
      keyPoints: [
        "Line symmetry: count the mirror lines where both sides match exactly.",
        "Rotational symmetry: order = number of times a shape fits onto itself in one full turn.",
        "Order 1 means no rotational symmetry beyond the starting position.",
        "A regular polygon has the same number of lines of symmetry as sides.",
        "A regular polygon also has rotational symmetry order equal to its number of sides.",
      ],
      workedExamples: [
        {
          problem: "How many lines of symmetry does a regular hexagon have?",
          image: PAGE_IMAGES.p100_101,
          steps: [
            {
              step: 1,
              instruction: "Use the regular polygon rule.",
              working: "A regular polygon has one line of symmetry for each side.",
            },
            {
              step: 2,
              instruction: "Count the sides.",
              working: "A hexagon has 6 sides.",
            },
            {
              step: 3,
              instruction: "State the answer.",
              working: "So it has 6 lines of symmetry.",
            },
          ],
          answer: "6",
        },
        {
          problem: "What is the order of rotational symmetry of a regular pentagon?",
          steps: [
            {
              step: 1,
              instruction: "Use the regular polygon rule.",
              working: "Rotational symmetry order = number of sides.",
            },
            {
              step: 2,
              instruction: "A pentagon has 5 sides.",
              working: "Order = 5.",
            },
          ],
          answer: "5",
        },
      ],
      whyItWorks:
        "Symmetry is about exact matching. Regular polygons are evenly spaced around their centre, so each side or vertex can map exactly onto another.",
    },
    practice: {
      warmUp: [
        {
          id: "5.1.w1",
          type: "number",
          question: "How many lines of symmetry does a square have?",
          answer: "4",
          hint1: "Think vertical, horizontal and diagonals.",
          hint2: "A square is a regular quadrilateral.",
          hint3: "4",
        },
      ],
      level1: [
        {
          id: "5.1.1",
          type: "number",
          question: "How many lines of symmetry does a rectangle have?",
          answer: "2",
          hint1: "A non-square rectangle folds horizontally and vertically.",
          hint2: "It does not fold correctly along the diagonals.",
          hint3: "2",
        },
        {
          id: "5.1.2",
          type: "number",
          question: "What is the order of rotational symmetry of an equilateral triangle?",
          answer: "3",
          hint1: "It fits after turns of 120°, 240° and 360°.",
          hint2: "Count the positions where it looks identical.",
          hint3: "3",
        },
      ],
      level2: [
        {
          id: "5.1.3",
          type: "number",
          question: "A regular 20-sided polygon has how many lines of symmetry?",
          answer: "20",
          hint1: "Regular polygon rule.",
          hint2: "Lines of symmetry = number of sides.",
          hint3: "20",
        },
        {
          id: "5.1.4",
          type: "choice",
          question: "Which quadrilateral has no lines of symmetry but rotational symmetry of order 2?",
          options: ["Square", "Rectangle", "Parallelogram", "Kite"],
          answer: "Parallelogram",
          hint1: "A general parallelogram has no mirror line.",
          hint2: "It maps onto itself after a 180° turn.",
          hint3: "Parallelogram",
        },
      ],
      level3: [
        {
          id: "5.1.5",
          type: "choice",
          question: "A shape has rotational symmetry of order 4. Which angle of rotation must map it onto itself?",
          options: ["45°", "60°", "90°", "180° only"],
          answer: "90°",
          hint1: "Divide 360° by the order.",
          hint2: "360 ÷ 4 = 90.",
          hint3: "90°",
        },
      ],
    },
  },

  {
    id: "5.2",
    title: "Perimeter, Area & Compound Shapes",
    slug: "perimeter-area",
    pageRef: "Pages 104-107",
    difficulty: "foundation",
    learn: {
      explanation:
        "Perimeter is the distance around the outside of a shape. Area is the space inside a shape. Compound area questions usually require you to split the shape into rectangles, triangles, parallelograms or trapezia.",
      keyPoints: [
        "Rectangle: area = length × width.",
        "Triangle: area = ½ × base × perpendicular height.",
        "Parallelogram: area = base × perpendicular height.",
        "Trapezium: area = ½(a + b)h, where a and b are the parallel sides.",
        "Compound shapes: split into simple shapes, calculate each area, then add or subtract.",
        "Always include squared units for area, such as cm² or m².",
      ],
      workedExamples: [
        {
          problem: "Find the area of a compound shape made from a rectangle 6 cm by 2 cm and a triangle with base 6 cm and height 5 cm.",
          image: PAGE_IMAGES.p104_105,
          steps: [
            {
              step: 1,
              instruction: "Find the rectangle area.",
              working: "6 × 2 = 12 cm²",
            },
            {
              step: 2,
              instruction: "Find the triangle area.",
              working: "½ × 6 × 5 = 15 cm²",
            },
            {
              step: 3,
              instruction: "Add the areas.",
              working: "12 + 15 = 27 cm²",
            },
          ],
          answer: "27 cm²",
        },
        {
          problem: "A square has perimeter 28 cm. Find its area.",
          steps: [
            {
              step: 1,
              instruction: "Find one side.",
              working: "28 ÷ 4 = 7 cm",
            },
            {
              step: 2,
              instruction: "Square the side length.",
              working: "7 × 7 = 49 cm²",
            },
          ],
          answer: "49 cm²",
        },
      ],
      whyItWorks:
        "Most area formulae are built from rectangles. Triangles are half of a rectangle or parallelogram, and trapezia can be treated as an average width multiplied by height.",
    },
    practice: {
      warmUp: [
        {
          id: "5.2.w1",
          type: "number",
          question: "Find the area of a parallelogram with base 8 cm and height 5 cm.",
          answer: "40",
          hint1: "Area = base × perpendicular height.",
          hint2: "8 × 5",
          hint3: "40 cm²",
        },
      ],
      level1: [
        {
          id: "5.2.1",
          type: "number",
          question: "Find the area of a triangle with base 10 cm and height 6 cm.",
          answer: "30",
          hint1: "Area = ½ × base × height.",
          hint2: "½ × 10 × 6",
          hint3: "30 cm²",
        },
        {
          id: "5.2.2",
          type: "number",
          question: "Find the area of a trapezium with parallel sides 7 cm and 3 cm, and height 3 cm.",
          answer: "15",
          hint1: "Area = ½(a + b)h.",
          hint2: "½(7 + 3) × 3",
          hint3: "15 cm²",
        },
      ],
      level2: [
        {
          id: "5.2.3",
          type: "number",
          question: "A square has perimeter 20 cm. What is its area?",
          answer: "25",
          hint1: "Side length = 20 ÷ 4.",
          hint2: "Side length = 5 cm.",
          hint3: "Area = 5 × 5 = 25 cm².",
        },
        {
          id: "5.2.4",
          type: "number",
          question: "A rectangle has area 48 cm² and width 6 cm. Find its length.",
          answer: "8",
          hint1: "Area = length × width.",
          hint2: "48 = length × 6.",
          hint3: "Length = 8 cm.",
        },
      ],
      level3: [
        {
          id: "5.2.5",
          type: "number",
          question: "A compound shape is made from a 10 cm by 6 cm rectangle with a 4 cm by 3 cm rectangle cut out. Find the remaining area.",
          answer: "48",
          hint1: "Find the large rectangle area.",
          hint2: "10 × 6 = 60, and 4 × 3 = 12.",
          hint3: "60 − 12 = 48 cm².",
        },
      ],
    },
  },

  {
    id: "5.3",
    title: "Circles",
    slug: "circles",
    pageRef: "Pages 106-109",
    difficulty: "intermediate",
    learn: {
      explanation:
        "The circumference of a circle is its perimeter. The area is the space inside it. The key formulae are C = πd, C = 2πr and A = πr².",
      keyPoints: [
        "Radius = distance from centre to circumference.",
        "Diameter = 2 × radius.",
        "Circumference = π × diameter.",
        "Circumference = 2πr.",
        "Area = πr².",
        "A tangent touches a circle at exactly one point.",
        "A chord is a straight line joining two points on the circumference.",
        "A sector is a slice of a circle.",
        "A segment is the region between a chord and an arc.",
      ],
      workedExamples: [
        {
          problem: "Find the circumference and area of a circle with radius 6 cm.",
          image: PAGE_IMAGES.p106_107,
          steps: [
            {
              step: 1,
              instruction: "Use circumference = 2πr.",
              working: "2 × π × 6 = 37.7 cm to 1 d.p.",
            },
            {
              step: 2,
              instruction: "Use area = πr².",
              working: "π × 6² = π × 36 = 113.1 cm² to 1 d.p.",
            },
          ],
          answer: "Circumference = 37.7 cm, area = 113.1 cm²",
        },
        {
          problem: "A circle has diameter 14 cm. Find its circumference to 1 d.p.",
          steps: [
            {
              step: 1,
              instruction: "Use C = πd.",
              working: "C = π × 14",
            },
            {
              step: 2,
              instruction: "Calculate.",
              working: "C = 44.0 cm to 1 d.p.",
            },
          ],
          answer: "44.0 cm",
        },
      ],
      whyItWorks:
        "π is the constant ratio between a circle’s circumference and diameter. Every circle, large or small, has the same ratio.",
    },
    practice: {
      level1: [
        {
          id: "5.3.1",
          type: "number",
          question: "Find the circumference of a circle with diameter 10 cm, to 1 d.p.",
          answer: "31.4",
          hint1: "Use C = πd.",
          hint2: "π × 10",
          hint3: "31.4 cm",
        },
        {
          id: "5.3.2",
          type: "number",
          question: "Find the area of a circle with radius 5 cm, to 1 d.p.",
          answer: "78.5",
          hint1: "Use A = πr².",
          hint2: "π × 5² = π × 25.",
          hint3: "78.5 cm²",
        },
      ],
      level2: [
        {
          id: "5.3.3",
          type: "choice",
          question: "What is a tangent?",
          options: [
            "A line across the circle",
            "A line that touches the circle at exactly one point",
            "The distance around the circle",
            "Half the diameter",
          ],
          answer: "A line that touches the circle at exactly one point",
          hint1: "A tangent just touches the circle.",
          hint2: "It meets the circumference at one point only.",
          hint3: "A tangent touches at exactly one point.",
        },
        {
          id: "5.3.4",
          type: "number",
          question: "A circle has area 50.3 cm². Find its radius to 1 d.p.",
          answer: "4.0",
          hint1: "Use A = πr².",
          hint2: "r² = 50.3 ÷ π.",
          hint3: "r ≈ 4.0 cm.",
        },
      ],
      level3: [
        {
          id: "5.3.5",
          type: "number",
          question: "A semicircle has radius 4 cm. Find its area to 1 d.p.",
          answer: "25.1",
          hint1: "Find the full circle area first.",
          hint2: "π × 4² = 50.3 cm².",
          hint3: "Half is 25.1 cm².",
        },
      ],
    },
  },

  {
    id: "5.4",
    title: "3D Shapes & Volume",
    slug: "3d-shapes-volume",
    pageRef: "Pages 109-115",
    difficulty: "intermediate",
    learn: {
      explanation:
        "Volume is the amount of space inside a 3D solid. For any prism, volume = cross-sectional area × length. Surface area is the total area of all outside faces.",
      keyPoints: [
        "Cuboid: volume = length × width × height.",
        "Prism: volume = cross-sectional area × length.",
        "Cylinder: volume = πr²h.",
        "Surface area is found by adding the areas of all faces.",
        "A cuboid has 6 faces, 12 edges and 8 vertices.",
        "A cube is a special cuboid where all edges are equal.",
      ],
      workedExamples: [
        {
          problem: "Find the volume of a cylinder with radius 3 cm and height 10 cm.",
          image: PAGE_IMAGES.p112_113,
          steps: [
            {
              step: 1,
              instruction: "Use V = πr²h.",
              working: "V = π × 3² × 10",
            },
            {
              step: 2,
              instruction: "Calculate.",
              working: "V = π × 9 × 10 = 282.7 cm³ to 1 d.p.",
            },
          ],
          answer: "282.7 cm³",
        },
        {
          problem: "Find the volume of a triangular prism with triangular cross-section base 6 cm, height 4 cm and length 10 cm.",
          steps: [
            {
              step: 1,
              instruction: "Find cross-sectional area.",
              working: "½ × 6 × 4 = 12 cm²",
            },
            {
              step: 2,
              instruction: "Multiply by length.",
              working: "12 × 10 = 120 cm³",
            },
          ],
          answer: "120 cm³",
        },
      ],
      whyItWorks:
        "A prism has the same cross-section all the way through. Multiplying the cross-section by the length counts how many layers of that shape fill the solid.",
    },
    practice: {
      warmUp: [
        {
          id: "5.4.w1",
          type: "number",
          question: "How many edges does a cube have?",
          answer: "12",
          hint1: "A cube has 6 faces.",
          hint2: "There are 4 top edges, 4 bottom edges and 4 vertical edges.",
          hint3: "12",
        },
        {
          id: "5.4.w2",
          type: "number",
          question: "How many vertices does a cuboid have?",
          answer: "8",
          hint1: "Vertices are corners.",
          hint2: "4 on top and 4 on the bottom.",
          hint3: "8",
        },
      ],
      level1: [
        {
          id: "5.4.1",
          type: "number",
          question: "Find the volume of a cuboid measuring 5 cm × 3 cm × 8 cm.",
          answer: "120",
          hint1: "Volume = length × width × height.",
          hint2: "5 × 3 × 8",
          hint3: "120 cm³",
        },
      ],
      level2: [
        {
          id: "5.4.2",
          type: "number",
          question: "Find the surface area of a cube with side length 4 cm.",
          answer: "96",
          hint1: "A cube has 6 equal square faces.",
          hint2: "Each face has area 4 × 4 = 16 cm².",
          hint3: "6 × 16 = 96 cm².",
        },
        {
          id: "5.4.3",
          type: "number",
          question: "Find the volume of a cylinder with radius 2 cm and height 5 cm, to 1 d.p.",
          answer: "62.8",
          hint1: "Use V = πr²h.",
          hint2: "π × 2² × 5 = 20π.",
          hint3: "62.8 cm³.",
        },
      ],
      level3: [
        {
          id: "5.4.4",
          type: "number",
          question: "A prism has volume 180 cm³ and cross-sectional area 15 cm². Find its length.",
          answer: "12",
          hint1: "Volume = cross-sectional area × length.",
          hint2: "180 = 15 × length.",
          hint3: "Length = 12 cm.",
        },
      ],
    },
  },

  {
    id: "5.5",
    title: "Angle Rules & Parallel Lines",
    slug: "angle-rules",
    pageRef: "Pages 114-121",
    difficulty: "foundation",
    learn: {
      explanation:
        "Angle questions depend on recognising standard rules: triangles, straight lines, points, quadrilaterals, polygons and parallel lines.",
      keyPoints: [
        "Angles in a triangle add to 180°.",
        "Angles on a straight line add to 180°.",
        "Angles in a quadrilateral add to 360°.",
        "Angles around a point add to 360°.",
        "Vertically opposite angles are equal.",
        "Alternate angles on parallel lines are equal.",
        "Corresponding angles on parallel lines are equal.",
        "Co-interior angles on parallel lines add to 180°.",
        "Exterior angle of a regular polygon = 360° ÷ number of sides.",
        "Interior angle of a regular polygon = 180° − exterior angle.",
      ],
      workedExamples: [
        {
          problem: "Find angle x in a triangle with angles 75°, 75° and x°.",
          image: PAGE_IMAGES.p116_117,
          steps: [
            {
              step: 1,
              instruction: "Use the triangle angle sum.",
              working: "75 + 75 + x = 180",
            },
            {
              step: 2,
              instruction: "Solve.",
              working: "x = 180 − 150 = 30°",
            },
          ],
          answer: "30°",
        },
        {
          problem: "Find the interior angle of a regular octagon.",
          steps: [
            {
              step: 1,
              instruction: "Find the exterior angle.",
              working: "360 ÷ 8 = 45°",
            },
            {
              step: 2,
              instruction: "Subtract from 180°.",
              working: "180 − 45 = 135°",
            },
          ],
          answer: "135°",
        },
      ],
      whyItWorks:
        "Angle rules come from turns, straight lines and parallel line geometry. Many GCSE questions combine two or more rules in the same diagram.",
    },
    practice: {
      level1: [
        {
          id: "5.5.1",
          type: "number",
          question: "Find the missing angle in a triangle with angles 60° and 80°.",
          answer: "40",
          hint1: "Angles in a triangle add to 180°.",
          hint2: "180 − 60 − 80",
          hint3: "40°",
        },
        {
          id: "5.5.2",
          type: "number",
          question: "Angles on a straight line: one angle is 115°. Find the other.",
          answer: "65",
          hint1: "Angles on a straight line add to 180°.",
          hint2: "180 − 115",
          hint3: "65°",
        },
      ],
      level2: [
        {
          id: "5.5.3",
          type: "number",
          question: "Find the interior angle of a regular hexagon.",
          answer: "120",
          hint1: "Exterior angle = 360 ÷ 6.",
          hint2: "Exterior angle = 60°.",
          hint3: "Interior angle = 180 − 60 = 120°.",
        },
        {
          id: "5.5.4",
          type: "number",
          question: "Find the sum of the interior angles of a 7-sided polygon.",
          answer: "900",
          hint1: "Use (n − 2) × 180.",
          hint2: "(7 − 2) × 180",
          hint3: "900°",
        },
      ],
      level3: [
        {
          id: "5.5.5",
          type: "number",
          question: "A regular polygon has interior angle 150°. How many sides does it have?",
          answer: "12",
          hint1: "Exterior angle = 180 − 150.",
          hint2: "Exterior angle = 30°.",
          hint3: "Number of sides = 360 ÷ 30 = 12.",
        },
        {
          id: "5.5.6",
          type: "number",
          question: "Two co-interior angles are 3x and 2x. Find x.",
          answer: "36",
          hint1: "Co-interior angles add to 180°.",
          hint2: "3x + 2x = 180.",
          hint3: "5x = 180, so x = 36.",
        },
      ],
    },
  },

  {
    id: "5.6",
    title: "Transformations",
    slug: "transformations",
    pageRef: "Pages 122-129",
    difficulty: "intermediate",
    learn: {
      explanation:
        "The four GCSE transformations are translation, reflection, rotation and enlargement. To describe a transformation fully, you must give all the required information.",
      keyPoints: [
        "Translation: describe using a column vector.",
        "Reflection: give the equation of the mirror line.",
        "Rotation: give angle, direction and centre of rotation.",
        "Enlargement: give scale factor and centre of enlargement.",
        "Congruent shapes are the same size and shape.",
        "Similar shapes are the same shape but may be different sizes.",
        "A negative scale factor enlarges through the centre of enlargement and reverses orientation.",
      ],
      workedExamples: [
        {
          problem: "Describe the transformation from A to B if B is A moved 5 left and 3 down.",
          image: PAGE_IMAGES.p122_123,
          steps: [
            {
              step: 1,
              instruction: "Identify the transformation.",
              working: "It is a translation because the shape has slid without turning, flipping or resizing.",
            },
            {
              step: 2,
              instruction: "Write the movement as a vector.",
              working: "5 left = −5, 3 down = −3.",
            },
          ],
          answer: "Translation by vector (-5, -3)",
        },
        {
          problem: "A shape is reflected in the y-axis. What is the equation of the mirror line?",
          steps: [
            {
              step: 1,
              instruction: "Identify the y-axis equation.",
              working: "The y-axis is the vertical line where x = 0.",
            },
          ],
          answer: "x = 0",
        },
      ],
      whyItWorks:
        "Transformations describe exact changes to position, orientation and size. GCSE questions often require both performing and describing the transformation.",
    },
    practice: {
      level1: [
        {
          id: "5.6.1",
          type: "choice",
          question: "What information is needed to describe a rotation fully?",
          options: ["Angle only", "Angle, direction and centre", "Mirror line", "Scale factor and centre"],
          answer: "Angle, direction and centre",
          hint1: "You need three pieces of information.",
          hint2: "How far, which way, and around what point.",
          hint3: "Angle, direction and centre.",
        },
        {
          id: "5.6.2",
          type: "choice",
          question: "A reflection must be described using what?",
          options: ["A vector", "A mirror line", "A scale factor", "A centre only"],
          answer: "A mirror line",
          hint1: "Reflection means mirror image.",
          hint2: "You need the line of reflection.",
          hint3: "A mirror line.",
        },
      ],
      level2: [
        {
          id: "5.6.3",
          type: "choice",
          question: "Triangle congruence: what does SAS stand for?",
          options: ["Side-Angle-Side", "Same-Area-Shape", "Side-Area-Side", "Similar-And-Same"],
          answer: "Side-Angle-Side",
          hint1: "Two sides and the angle between them.",
          hint2: "The angle must be included between the two sides.",
          hint3: "Side-Angle-Side.",
        },
        {
          id: "5.6.4",
          type: "choice",
          question: "An enlargement with scale factor 3 changes area by what factor?",
          options: ["3", "6", "9", "27"],
          answer: "9",
          hint1: "Area scale factor is the square of the length scale factor.",
          hint2: "3² = 9.",
          hint3: "9",
        },
      ],
      level3: [
        {
          id: "5.6.5",
          type: "choice",
          question: "Which transformation can change the size of a shape?",
          options: ["Translation", "Reflection", "Rotation", "Enlargement"],
          answer: "Enlargement",
          hint1: "Three transformations preserve size.",
          hint2: "Only one changes length scale.",
          hint3: "Enlargement.",
        },
      ],
    },
  },

  {
    id: "5.7",
    title: "Pythagoras' Theorem",
    slug: "pythagoras",
    pageRef: "Pages 130-131",
    difficulty: "intermediate",
    learn: {
      explanation:
        "Pythagoras' theorem works for right-angled triangles only. If c is the hypotenuse, then a² + b² = c².",
      keyPoints: [
        "Only use Pythagoras in right-angled triangles.",
        "The hypotenuse is the longest side, opposite the right angle.",
        "To find the hypotenuse: square the two shorter sides, add, then square root.",
        "To find a shorter side: square the hypotenuse, subtract the other shorter side squared, then square root.",
        "Common triples include 3-4-5, 5-12-13 and 8-15-17.",
      ],
      workedExamples: [
        {
          problem: "Find the hypotenuse of a right-angled triangle with shorter sides 6 cm and 8 cm.",
          image: PAGE_IMAGES.p130_131,
          steps: [
            {
              step: 1,
              instruction: "Square both shorter sides.",
              working: "6² = 36 and 8² = 64",
            },
            {
              step: 2,
              instruction: "Add.",
              working: "36 + 64 = 100",
            },
            {
              step: 3,
              instruction: "Square root.",
              working: "√100 = 10",
            },
          ],
          answer: "10 cm",
        },
        {
          problem: "Find the missing shorter side when the hypotenuse is 13 cm and one shorter side is 5 cm.",
          steps: [
            {
              step: 1,
              instruction: "Square the hypotenuse and known side.",
              working: "13² = 169 and 5² = 25",
            },
            {
              step: 2,
              instruction: "Subtract.",
              working: "169 − 25 = 144",
            },
            {
              step: 3,
              instruction: "Square root.",
              working: "√144 = 12",
            },
          ],
          answer: "12 cm",
        },
      ],
      whyItWorks:
        "In any right-angled triangle, the square on the hypotenuse has the same area as the two squares on the shorter sides combined.",
    },
    practice: {
      level1: [
        {
          id: "5.7.1",
          type: "number",
          question: "Find the hypotenuse of a right-angled triangle with shorter sides 3 cm and 4 cm.",
          answer: "5",
          hint1: "3² + 4² = 9 + 16.",
          hint2: "√25",
          hint3: "5 cm",
        },
        {
          id: "5.7.2",
          type: "number",
          question: "Find the hypotenuse of a right-angled triangle with shorter sides 5 cm and 12 cm.",
          answer: "13",
          hint1: "5² + 12² = 25 + 144.",
          hint2: "√169",
          hint3: "13 cm",
        },
      ],
      level2: [
        {
          id: "5.7.3",
          type: "number",
          question: "Find the missing side: hypotenuse 10 cm, one shorter side 8 cm.",
          answer: "6",
          hint1: "10² − 8² = 100 − 64.",
          hint2: "√36",
          hint3: "6 cm",
        },
        {
          id: "5.7.4",
          type: "choice",
          question: "Is a triangle with sides 8, 15 and 17 right-angled?",
          options: ["Yes", "No"],
          answer: "Yes",
          hint1: "Check the two shorter sides.",
          hint2: "8² + 15² = 64 + 225 = 289.",
          hint3: "17² = 289, so yes.",
        },
      ],
      level3: [
        {
          id: "5.7.5",
          type: "number",
          question: "A ladder is 10 m long and its base is 3 m from a wall. How high up the wall does it reach, to 2 d.p.?",
          answer: "9.54",
          hint1: "The ladder is the hypotenuse.",
          hint2: "10² − 3² = 100 − 9 = 91.",
          hint3: "√91 = 9.54 m.",
        },
        {
          id: "5.7.6",
          type: "number",
          question: "Two points are A(1, 2) and B(7, 10). Find the distance AB.",
          answer: "10",
          hint1: "Horizontal change = 6, vertical change = 8.",
          hint2: "Use Pythagoras: √(6² + 8²).",
          hint3: "√100 = 10.",
        },
      ],
    },
  },

  {
    id: "5.8",
    title: "Trigonometry (SOH CAH TOA)",
    slug: "trigonometry",
    pageRef: "Pages 132-137",
    difficulty: "higher",
    learn: {
      explanation:
        "SOH CAH TOA is used for right-angled triangle trigonometry. It connects angles to side ratios.",
      keyPoints: [
        "SOH: sin θ = opposite ÷ hypotenuse.",
        "CAH: cos θ = adjacent ÷ hypotenuse.",
        "TOA: tan θ = opposite ÷ adjacent.",
        "To find a missing angle, use inverse sin, inverse cos or inverse tan.",
        "Always label the hypotenuse first.",
        "Opposite is across from the angle being used.",
        "Adjacent is next to the angle and is not the hypotenuse.",
      ],
      workedExamples: [
        {
          problem: "Find x if the hypotenuse is 12 cm, the angle is 40°, and x is the opposite side.",
          image: PAGE_IMAGES.p132_133,
          steps: [
            {
              step: 1,
              instruction: "Choose the ratio.",
              working: "Opposite and hypotenuse means SOH.",
            },
            {
              step: 2,
              instruction: "Set up the equation.",
              working: "sin 40° = x ÷ 12",
            },
            {
              step: 3,
              instruction: "Rearrange.",
              working: "x = 12 × sin 40°",
            },
            {
              step: 4,
              instruction: "Calculate.",
              working: "x = 7.7 cm to 1 d.p.",
            },
          ],
          answer: "7.7 cm",
        },
        {
          problem: "Find angle θ if the opposite side is 5 cm and the adjacent side is 9 cm.",
          steps: [
            {
              step: 1,
              instruction: "Choose the ratio.",
              working: "Opposite and adjacent means TOA.",
            },
            {
              step: 2,
              instruction: "Set up.",
              working: "tan θ = 5 ÷ 9",
            },
            {
              step: 3,
              instruction: "Use inverse tan.",
              working: "θ = tan⁻¹(5 ÷ 9) = 29.1° to 1 d.p.",
            },
          ],
          answer: "29.1°",
        },
      ],
      whyItWorks:
        "For any fixed angle, similar right-angled triangles have the same side ratios. Trigonometry uses those constant ratios.",
    },
    practice: {
      level1: [
        {
          id: "5.8.1",
          type: "choice",
          question: "Which formula involves the adjacent side and the hypotenuse?",
          options: ["SOH", "CAH", "TOA"],
          answer: "CAH",
          hint1: "C-A-H",
          hint2: "Cos = adjacent ÷ hypotenuse.",
          hint3: "CAH",
        },
      ],
      level2: [
        {
          id: "5.8.2",
          type: "number",
          question: "A ramp is 2.6 m long and 1.5 m high. Find the angle to the ground, to 1 d.p.",
          answer: "35.2",
          hint1: "Opposite = 1.5, hypotenuse = 2.6.",
          hint2: "sin θ = 1.5 ÷ 2.6.",
          hint3: "θ = sin⁻¹(0.5769...) = 35.2°.",
        },
        {
          id: "5.8.3",
          type: "number",
          question: "A right-angled triangle has adjacent side 7 cm and angle 30°. Find the hypotenuse to 1 d.p.",
          answer: "8.1",
          hint1: "Adjacent and hypotenuse means cosine.",
          hint2: "cos 30° = 7 ÷ h.",
          hint3: "h = 7 ÷ cos 30° = 8.1 cm.",
        },
      ],
      level3: [
        {
          id: "5.8.4",
          type: "number",
          question: "A church steeple is 9 m from an observer. The angle of elevation is 55°. Find the height, to 1 d.p.",
          answer: "12.9",
          hint1: "Opposite and adjacent means tangent.",
          hint2: "tan 55° = height ÷ 9.",
          hint3: "height = 9 × tan 55° = 12.9 m.",
        },
      ],
    },
  },

  {
    id: "5.9",
    title: "Sine Rule",
    slug: "sine-rule",
    pageRef: "Higher expansion",
    difficulty: "higher",
    learn: {
      explanation:
        "The sine rule works in any triangle, not just right-angled triangles. Use it when you know a matching side-angle pair and one more side or angle.",
      keyPoints: [
        "Sine rule: a/sin A = b/sin B = c/sin C.",
        "Sides a, b and c are opposite angles A, B and C.",
        "Use when you have a side and its opposite angle pair.",
        "To find a side: a = b × sin A ÷ sin B.",
        "To find an angle: sin A = a × sin B ÷ b, then use sin⁻¹.",
        "Check for the ambiguous case when finding an angle from SSA information.",
      ],
      workedExamples: [
        {
          problem: "Triangle ABC has A = 50°, B = 60° and side a = 8 cm. Find side b to 1 d.p.",
          steps: [
            {
              step: 1,
              instruction: "Set up the sine rule.",
              working: "8/sin 50° = b/sin 60°",
            },
            {
              step: 2,
              instruction: "Rearrange for b.",
              working: "b = 8 × sin 60° ÷ sin 50°",
            },
            {
              step: 3,
              instruction: "Calculate.",
              working: "b ≈ 9.0 cm",
            },
          ],
          answer: "9.0 cm",
        },
        {
          problem: "Triangle has a = 7 cm, A = 30° and b = 10 cm. Find angle B to 1 d.p.",
          steps: [
            {
              step: 1,
              instruction: "Set up.",
              working: "sin B / 10 = sin 30° / 7",
            },
            {
              step: 2,
              instruction: "Rearrange.",
              working: "sin B = 10 × sin 30° ÷ 7 = 0.714...",
            },
            {
              step: 3,
              instruction: "Use inverse sine.",
              working: "B = sin⁻¹(0.714...) ≈ 45.6°",
            },
          ],
          answer: "45.6°",
        },
      ],
      whyItWorks:
        "The sine rule comes from splitting a triangle into right-angled triangles and comparing the height calculated from different sides.",
    },
    practice: {
      warmUp: [
        {
          id: "5.9.w1",
          type: "number",
          question: "What is sin 30°?",
          answer: "0.5",
          hint1: "This is a standard exact value.",
          hint2: "sin 30° = 1/2.",
          hint3: "0.5",
        },
        {
          id: "5.9.w2",
          type: "number",
          question: "If a/sin A = b/sin B, a = 10, sin A = 0.5 and sin B = 0.25, find b.",
          answer: "5",
          hint1: "10 ÷ 0.5 = 20.",
          hint2: "b = 20 × 0.25.",
          hint3: "5",
        },
      ],
      level1: [
        {
          id: "5.9.1",
          type: "number",
          question: "Triangle: A = 40°, a = 5, B = 70°. Find side b to 1 d.p.",
          answer: "7.3",
          hint1: "5/sin 40° = b/sin 70°.",
          hint2: "b = 5 × sin 70° ÷ sin 40°.",
          hint3: "b ≈ 7.3.",
        },
      ],
      level2: [
        {
          id: "5.9.2",
          type: "number",
          question: "Triangle: a = 12, A = 50°, b = 9. Find angle B to 1 d.p.",
          answer: "35.1",
          hint1: "sin B / 9 = sin 50° / 12.",
          hint2: "sin B = 9 × sin 50° ÷ 12.",
          hint3: "B ≈ 35.1°.",
        },
      ],
      level3: [
        {
          id: "5.9.3",
          type: "number",
          question: "Triangle ABC: A = 35°, B = 75°, side a = 8 cm. Find side c to 1 d.p.",
          answer: "13.1",
          hint1: "C = 180 − 35 − 75 = 70°.",
          hint2: "c/sin 70° = 8/sin 35°.",
          hint3: "c ≈ 8 × sin 70° ÷ sin 35° ≈ 13.1 cm.",
        },
      ],
    },
    aiChallenge: {
      prompt:
        "Triangle PQR has angle P = 45°, side p = 10 cm opposite P and side q = 8 cm opposite Q. Find angle Q and explain whether the triangle is uniquely determined.",
      successCriteria: [
        "Sets up sine rule: sin Q / 8 = sin 45° / 10.",
        "Calculates sin Q = 8 × sin 45° ÷ 10 ≈ 0.566.",
        "Finds Q ≈ 34.4°.",
        "Considers the ambiguous case: 180 − 34.4 = 145.6°.",
        "Rejects 145.6° because 145.6° + 45° is greater than 180°.",
        "Concludes the valid triangle is unique.",
      ],
    },
  },

  {
    id: "5.10",
    title: "Cosine Rule",
    slug: "cosine-rule",
    pageRef: "Higher expansion",
    difficulty: "higher",
    learn: {
      explanation:
        "The cosine rule works in any triangle. It is used when you know two sides and the included angle, or when you know all three sides and need an angle.",
      keyPoints: [
        "To find a side: a² = b² + c² − 2bc cos A.",
        "To find an angle: cos A = (b² + c² − a²) ÷ 2bc.",
        "Use it for two sides and the included angle.",
        "Use it for all three sides.",
        "When A = 90°, cos A = 0 and the formula becomes Pythagoras.",
        "The largest angle is opposite the longest side.",
      ],
      workedExamples: [
        {
          problem: "Two sides are 7 cm and 9 cm with included angle 60°. Find the opposite side c.",
          steps: [
            {
              step: 1,
              instruction: "Use the cosine rule.",
              working: "c² = 7² + 9² − 2(7)(9)cos 60°",
            },
            {
              step: 2,
              instruction: "Calculate.",
              working: "c² = 49 + 81 − 126(0.5) = 67",
            },
            {
              step: 3,
              instruction: "Square root.",
              working: "c = √67 ≈ 8.2 cm",
            },
          ],
          answer: "8.2 cm",
        },
        {
          problem: "Find angle A in a triangle with a = 6, b = 8 and c = 10.",
          steps: [
            {
              step: 1,
              instruction: "Use the rearranged cosine rule.",
              working: "cos A = (8² + 10² − 6²) ÷ (2 × 8 × 10)",
            },
            {
              step: 2,
              instruction: "Calculate.",
              working: "cos A = 128 ÷ 160 = 0.8",
            },
            {
              step: 3,
              instruction: "Use inverse cosine.",
              working: "A = cos⁻¹(0.8) ≈ 36.9°",
            },
          ],
          answer: "36.9°",
        },
      ],
      whyItWorks:
        "The cosine rule corrects Pythagoras for non-right-angled triangles. The correction term 2bc cos A accounts for the angle between the two known sides.",
    },
    practice: {
      warmUp: [
        {
          id: "5.10.w1",
          type: "choice",
          question: "When should you use the cosine rule?",
          options: [
            "One angle and its opposite side only",
            "Two sides with the included angle, or all three sides",
            "Right-angled triangles only",
          ],
          answer: "Two sides with the included angle, or all three sides",
          hint1: "There are two main cosine rule cases.",
          hint2: "SAS or SSS.",
          hint3: "Two sides with included angle, or all three sides.",
        },
      ],
      level1: [
        {
          id: "5.10.1",
          type: "number",
          question: "Triangle: sides 5 and 7 with included angle 90°. Find the opposite side to 1 d.p.",
          answer: "8.6",
          hint1: "cos 90° = 0.",
          hint2: "c² = 5² + 7².",
          hint3: "c = √74 = 8.6.",
        },
      ],
      level2: [
        {
          id: "5.10.2",
          type: "number",
          question: "Triangle: sides 5 and 7 with included angle 45°. Find the opposite side to 1 d.p.",
          answer: "5.0",
          hint1: "c² = 5² + 7² − 2(5)(7)cos45°.",
          hint2: "c² ≈ 74 − 49.5 = 24.5.",
          hint3: "c ≈ 5.0.",
        },
      ],
      level3: [
        {
          id: "5.10.3",
          type: "number",
          question: "Find the largest angle in a triangle with sides 5, 6 and 9, to 1 d.p.",
          answer: "109.5",
          hint1: "Largest angle is opposite side 9.",
          hint2: "cos A = (5² + 6² − 9²) ÷ (2 × 5 × 6).",
          hint3: "cos A = −20/60, so A ≈ 109.5°.",
        },
      ],
    },
    aiChallenge: {
      prompt:
        "Two ships leave port. Ship X travels 20 km on bearing 050° and Ship Y travels 15 km on bearing 110°. Find the distance between the two ships to 1 d.p.",
      successCriteria: [
        "Identifies the included angle as 110° − 50° = 60°.",
        "Selects the cosine rule because two sides and the included angle are known.",
        "Sets up d² = 20² + 15² − 2(20)(15)cos60°.",
        "Calculates d² = 400 + 225 − 300 = 325.",
        "States d = √325 ≈ 18.0 km.",
      ],
    },
  },

  {
    id: "5.11",
    title: "Area of a Triangle (½ab sin C)",
    slug: "area-sine-rule",
    pageRef: "Higher expansion",
    difficulty: "higher",
    learn: {
      explanation:
        "The formula area = ½ab sin C finds the area of any triangle when you know two sides and the included angle.",
      keyPoints: [
        "Formula: area = ½ab sin C.",
        "C must be the included angle between sides a and b.",
        "If C = 90°, sin C = 1, so the formula becomes ½ × base × height.",
        "Use this when the perpendicular height is not given.",
        "Check carefully that the angle is between the two known sides.",
      ],
      workedExamples: [
        {
          problem: "Triangle has sides 6 cm and 8 cm with included angle 30°. Find the area.",
          steps: [
            {
              step: 1,
              instruction: "Apply the formula.",
              working: "Area = ½ × 6 × 8 × sin30°",
            },
            {
              step: 2,
              instruction: "Use sin30° = 0.5.",
              working: "Area = 24 × 0.5",
            },
            {
              step: 3,
              instruction: "Calculate.",
              working: "Area = 12 cm²",
            },
          ],
          answer: "12 cm²",
        },
        {
          problem: "Triangle has sides 10 m and 14 m with included angle 75°. Find the area to 1 d.p.",
          steps: [
            {
              step: 1,
              instruction: "Apply the formula.",
              working: "Area = ½ × 10 × 14 × sin75°",
            },
            {
              step: 2,
              instruction: "Calculate.",
              working: "Area = 70 × 0.9659... = 67.6 m²",
            },
          ],
          answer: "67.6 m²",
        },
      ],
      whyItWorks:
        "Dropping a perpendicular height inside the triangle creates a right-angled triangle. The height can be written as a sin C, giving area = ½ × b × a sin C.",
    },
    practice: {
      warmUp: [
        {
          id: "5.11.w1",
          type: "number",
          question: "Find the area of a triangle with sides 4 and 10 and included angle 90°.",
          answer: "20",
          hint1: "sin90° = 1.",
          hint2: "½ × 4 × 10 × 1.",
          hint3: "20",
        },
      ],
      level1: [
        {
          id: "5.11.1",
          type: "number",
          question: "Find the area of a triangle with sides 8 and 12 and included angle 30°.",
          answer: "24",
          hint1: "sin30° = 0.5.",
          hint2: "½ × 8 × 12 × 0.5.",
          hint3: "24",
        },
        {
          id: "5.11.2",
          type: "number",
          question: "Find the area of a triangle with sides 5 and 12 and included angle 90°.",
          answer: "30",
          hint1: "sin90° = 1.",
          hint2: "½ × 5 × 12 × 1.",
          hint3: "30",
        },
      ],
      level2: [
        {
          id: "5.11.3",
          type: "number",
          question: "Find the area of a triangle with sides 9 and 11 and included angle 60°, to 1 d.p.",
          answer: "42.9",
          hint1: "Area = ½ × 9 × 11 × sin60°.",
          hint2: "sin60° ≈ 0.866.",
          hint3: "Area ≈ 42.9.",
        },
      ],
      level3: [
        {
          id: "5.11.4",
          type: "number",
          question: "A triangle has area 30 cm² and sides 10 cm and 12 cm. Find the acute included angle to 1 d.p.",
          answer: "30.0",
          hint1: "30 = ½ × 10 × 12 × sin C.",
          hint2: "30 = 60sin C.",
          hint3: "sin C = 0.5, so acute C = 30.0°.",
        },
      ],
    },
    aiChallenge: {
      prompt:
        "A field is triangular with sides 80 m and 110 m and an included angle of 65°. Find the area in hectares to 2 d.p. 1 hectare = 10,000 m².",
      successCriteria: [
        "Applies area = ½ × 80 × 110 × sin65°.",
        "Calculates area ≈ 3987.8 m².",
        "Converts by dividing by 10,000.",
        "States final answer ≈ 0.40 hectares.",
        "Includes units and appropriate rounding.",
      ],
    },
  },

  {
    id: "5.12",
    title: "Vectors",
    slug: "vectors",
    pageRef: "Higher expansion",
    difficulty: "higher",
    learn: {
      explanation:
        "A vector has magnitude and direction. Column vectors describe movement: the top number is horizontal movement and the bottom number is vertical movement.",
      keyPoints: [
        "Vector (3, 2) means 3 right and 2 up.",
        "Vector (-4, 1) means 4 left and 1 up.",
        "To add vectors, add matching components.",
        "To subtract vectors, subtract matching components.",
        "To multiply by a scalar, multiply each component.",
        "Magnitude of (x, y) is √(x² + y²).",
        "Vectors are parallel if one is a scalar multiple of the other.",
      ],
      workedExamples: [
        {
          problem: "Vectors a = (3, 1) and b = (-2, 4). Find a + b.",
          steps: [
            {
              step: 1,
              instruction: "Add components.",
              working: "(3 + -2, 1 + 4)",
            },
            {
              step: 2,
              instruction: "Simplify.",
              working: "(1, 5)",
            },
          ],
          answer: "(1,5)",
        },
        {
          problem: "If c = (4, 3), find 2c and the magnitude of c.",
          steps: [
            {
              step: 1,
              instruction: "Multiply each component by 2.",
              working: "2c = (8, 6)",
            },
            {
              step: 2,
              instruction: "Use Pythagoras for magnitude.",
              working: "|c| = √(4² + 3²) = √25 = 5",
            },
          ],
          answer: "2c = (8,6); |c| = 5",
        },
        {
          problem: "Are u = (2, 3) and v = (6, 9) parallel?",
          steps: [
            {
              step: 1,
              instruction: "Check for a scalar multiple.",
              working: "v = 3u because (6, 9) = 3(2, 3).",
            },
            {
              step: 2,
              instruction: "Conclude.",
              working: "They are parallel.",
            },
          ],
          answer: "Parallel",
        },
      ],
      whyItWorks:
        "A vector is a movement instruction. Adding vectors means doing one movement after another. Scaling a vector changes its length but not its direction unless the scale factor is negative.",
    },
    practice: {
      warmUp: [
        {
          id: "5.12.w1",
          type: "text",
          question: "Add (2, 5) + (3, 1). Write your answer in the form (a,b).",
          answer: "(5,6)",
          hint1: "Add the first components.",
          hint2: "Add the second components.",
          hint3: "(5,6)",
        },
        {
          id: "5.12.w2",
          type: "number",
          question: "Find the magnitude of vector (3, 4).",
          answer: "5",
          hint1: "Magnitude = √(x² + y²).",
          hint2: "√(3² + 4²) = √25.",
          hint3: "5",
        },
      ],
      level1: [
        {
          id: "5.12.1",
          type: "text",
          question: "If a = (4, -1), find 3a. Write your answer in the form (a,b).",
          answer: "(12,-3)",
          hint1: "Multiply each component by 3.",
          hint2: "(3 × 4, 3 × -1).",
          hint3: "(12,-3)",
        },
        {
          id: "5.12.2",
          type: "text",
          question: "Vectors p = (1, 2), q = (3, -4). Find p + q. Write your answer in the form (a,b).",
          answer: "(4,-2)",
          hint1: "Add components.",
          hint2: "(1 + 3, 2 + -4).",
          hint3: "(4,-2)",
        },
      ],
      level2: [
        {
          id: "5.12.3",
          type: "text",
          question: "Vectors a = (5, 2), b = (1, 6). Find 2a − b. Write your answer in the form (a,b).",
          answer: "(9,-2)",
          hint1: "2a = (10, 4).",
          hint2: "2a − b = (10 − 1, 4 − 6).",
          hint3: "(9,-2)",
        },
        {
          id: "5.12.4",
          type: "number",
          question: "Find the magnitude of vector (5, 12).",
          answer: "13",
          hint1: "Use √(x² + y²).",
          hint2: "√(25 + 144) = √169.",
          hint3: "13",
        },
      ],
      level3: [
        {
          id: "5.12.5",
          type: "choice",
          question: "Are u = (4, 6) and v = (-6, -9) parallel?",
          options: ["Parallel and same direction", "Parallel but opposite direction", "Not parallel"],
          answer: "Parallel but opposite direction",
          hint1: "Check if one is a multiple of the other.",
          hint2: "v = -1.5u.",
          hint3: "Negative multiple means opposite direction.",
        },
      ],
    },
    aiChallenge: {
      prompt:
        "Triangle OAB has vectors OA = a and OB = b. M is the midpoint of AB. Express vector OM in terms of a and b, explaining each step.",
      successCriteria: [
        "States AB = b − a.",
        "States AM = ½(b − a).",
        "Uses OM = OA + AM.",
        "Simplifies OM = a + ½(b − a).",
        "Final answer: OM = ½(a + b).",
      ],
    },
  },

  {
    id: "5.13",
    title: "Constructions & Loci",
    slug: "constructions-loci",
    pageRef: "GCSE expansion",
    difficulty: "foundation",
    learn: {
      explanation:
        "A construction is an accurate drawing using a ruler and pair of compasses. A locus is a set of points satisfying a rule, such as all points a fixed distance from a point.",
      keyPoints: [
        "Perpendicular bisector of AB: draw equal arcs from A and B, then join the arc intersections.",
        "Angle bisector: draw arcs from the vertex and then intersecting arcs from those arc points.",
        "Locus a fixed distance from a point = circle.",
        "Locus a fixed distance from a line = parallel line or pair of parallel lines.",
        "Equidistant from two points = perpendicular bisector.",
        "Equidistant from two intersecting lines = angle bisectors.",
        "For region questions, shade the overlap that satisfies all conditions.",
      ],
      workedExamples: [
        {
          problem: "Describe the locus of points 4 cm from a single point P.",
          steps: [
            {
              step: 1,
              instruction: "Identify the rule.",
              working: "All points are exactly 4 cm from P.",
            },
            {
              step: 2,
              instruction: "Match the locus.",
              working: "A fixed distance from one point forms a circle.",
            },
          ],
          answer: "A circle centred at P with radius 4 cm",
        },
        {
          problem: "Describe the locus of points equidistant from two posts A and B.",
          steps: [
            {
              step: 1,
              instruction: "Identify the rule.",
              working: "Same distance from A and B.",
            },
            {
              step: 2,
              instruction: "Match the construction.",
              working: "This is the perpendicular bisector of AB.",
            },
          ],
          answer: "The perpendicular bisector of AB",
        },
      ],
      whyItWorks:
        "Loci turn rules into diagrams. Constructions use equal distances from compass arcs, so they create exact geometric relationships.",
    },
    practice: {
      warmUp: [
        {
          id: "5.13.w1",
          type: "choice",
          question: "What is the locus of points 3 cm from point P?",
          options: ["A line", "A circle radius 3 cm", "A square"],
          answer: "A circle radius 3 cm",
          hint1: "Constant distance from one point.",
          hint2: "That forms a circle.",
          hint3: "Circle centred at P, radius 3 cm.",
        },
        {
          id: "5.13.w2",
          type: "choice",
          question: "What is the locus of points equidistant from two points A and B?",
          options: ["Line through A and B", "Perpendicular bisector of AB", "Angle bisector"],
          answer: "Perpendicular bisector of AB",
          hint1: "Equidistant from two points.",
          hint2: "Use the perpendicular bisector.",
          hint3: "Perpendicular bisector of AB.",
        },
      ],
      level1: [
        {
          id: "5.13.1",
          type: "choice",
          question: "Which tools are used to construct a perpendicular bisector?",
          options: ["Protractor only", "Pair of compasses and ruler", "Ruler only"],
          answer: "Pair of compasses and ruler",
          hint1: "Construction usually means no measuring angles.",
          hint2: "Use arcs and a straight line.",
          hint3: "Pair of compasses and ruler.",
        },
        {
          id: "5.13.2",
          type: "choice",
          question: "What is the locus of points 2 m from a straight wall on one side only?",
          options: ["A line parallel to the wall, 2 m away", "A circle", "A semicircle"],
          answer: "A line parallel to the wall, 2 m away",
          hint1: "Fixed distance from a straight line.",
          hint2: "That creates a parallel line.",
          hint3: "A line parallel to the wall, 2 m away.",
        },
      ],
      level2: [
        {
          id: "5.13.3",
          type: "choice",
          question: "What is the locus of points equidistant from two intersecting lines?",
          options: ["Perpendicular bisector", "Angle bisector(s)", "Circle"],
          answer: "Angle bisector(s)",
          hint1: "This is about two lines, not two points.",
          hint2: "The equal-distance lines split the angles.",
          hint3: "Angle bisectors.",
        },
        {
          id: "5.13.4",
          type: "choice",
          question: "A point must be within 5 m of point A and less than 3 m from a wall. Which region is correct?",
          options: ["The overlap of the two regions", "Either region", "Only the circle"],
          answer: "The overlap of the two regions",
          hint1: "The word 'and' means both conditions must be true.",
          hint2: "Look for the intersection.",
          hint3: "The overlap of the two regions.",
        },
      ],
      level3: [
        {
          id: "5.13.5",
          type: "number",
          question: "A goat is tied at the corner of a large field with a 5 m rope. It can graze a quarter-circle. Find the area to 1 d.p.",
          answer: "19.6",
          hint1: "Quarter-circle with radius 5.",
          hint2: "¼ × π × 5².",
          hint3: "19.6 m².",
        },
      ],
    },
    aiChallenge: {
      prompt:
        "A treasure is hidden in a 20 m by 30 m field. It is exactly 12 m from corner A and equidistant from sides AB and AD. Describe the loci and locate the treasure.",
      successCriteria: [
        "Identifies first locus as a circle centred at A with radius 12 m.",
        "Identifies second locus as the angle bisector of angle DAB.",
        "States the treasure is at the intersection of the two loci.",
        "Recognises there is one valid point inside the field.",
        "Calculates coordinates approximately (8.49 m, 8.49 m) from A along the two sides.",
      ],
    },
  },

  {
    id: "5.14",
    title: "Bearings",
    slug: "bearings",
    pageRef: "GCSE expansion",
    difficulty: "foundation",
    learn: {
      explanation:
        "A bearing is a direction measured clockwise from North. Bearings are always written with three digits, such as 045°, 090° or 270°.",
      keyPoints: [
        "Bearings are measured clockwise from North.",
        "Bearings are always written using three digits.",
        "North = 000°.",
        "East = 090°.",
        "South = 180°.",
        "West = 270°.",
        "Back bearing: add or subtract 180°.",
        "Draw a North line at the point you are measuring from.",
      ],
      workedExamples: [
        {
          problem: "Town B is due East of town A. What is the bearing of B from A?",
          steps: [
            {
              step: 1,
              instruction: "Use the compass direction.",
              working: "East is a quarter turn clockwise from North.",
            },
            {
              step: 2,
              instruction: "Write as a three-digit bearing.",
              working: "090°",
            },
          ],
          answer: "090°",
        },
        {
          problem: "The bearing of P from Q is 070°. What is the bearing of Q from P?",
          steps: [
            {
              step: 1,
              instruction: "Use the back-bearing rule.",
              working: "070° + 180° = 250°",
            },
          ],
          answer: "250°",
        },
      ],
      whyItWorks:
        "Bearings remove ambiguity by always starting from North and always measuring clockwise.",
    },
    practice: {
      warmUp: [
        {
          id: "5.14.w1",
          type: "text",
          question: "Write the bearing for due North.",
          answer: "000",
          hint1: "North is the starting direction.",
          hint2: "0° clockwise from North.",
          hint3: "000°",
        },
        {
          id: "5.14.w2",
          type: "text",
          question: "Write the bearing for due South.",
          answer: "180",
          hint1: "South is a half turn from North.",
          hint2: "A half turn is 180°.",
          hint3: "180°",
        },
      ],
      level1: [
        {
          id: "5.14.1",
          type: "text",
          question: "Write the bearing for due West.",
          answer: "270",
          hint1: "West is three quarters of a clockwise turn from North.",
          hint2: "270°",
          hint3: "270°",
        },
        {
          id: "5.14.2",
          type: "text",
          question: "The bearing of B from A is 060°. What is the bearing of A from B?",
          answer: "240",
          hint1: "Add 180° because 060° is less than 180°.",
          hint2: "060 + 180 = 240.",
          hint3: "240°",
        },
      ],
      level2: [
        {
          id: "5.14.3",
          type: "text",
          question: "The bearing of X from Y is 200°. What is the bearing of Y from X?",
          answer: "020",
          hint1: "Subtract 180° because 200° is greater than 180°.",
          hint2: "200 − 180 = 20.",
          hint3: "Write as 020°.",
        },
        {
          id: "5.14.4",
          type: "number",
          question: "Walking on a bearing of 050° means turning how many degrees clockwise from North?",
          answer: "50",
          hint1: "Use the definition of a bearing.",
          hint2: "050° means 50° clockwise from North.",
          hint3: "50°",
        },
      ],
      level3: [
        {
          id: "5.14.5",
          type: "number",
          question: "A ship sails on bearing 040° for 8 km, then on bearing 130° for 6 km. The turn creates a right-angled path. Find the distance from the start.",
          answer: "10",
          hint1: "The two directions differ by 90°.",
          hint2: "Use Pythagoras: √(8² + 6²).",
          hint3: "10 km",
        },
      ],
    },
    aiChallenge: {
      prompt:
        "A coastguard sees a boat on a bearing of 110° at 12 km away. A second coastguard, due south of the first, sees the same boat on a bearing of 040°. Sketch the situation and explain how to use the sine rule to find the distance between the coastguards.",
      successCriteria: [
        "Describes a triangle with both coastguards and the boat.",
        "Identifies the angle at the first coastguard between South and bearing 110° as 70°.",
        "Identifies the angle at the second coastguard between North and bearing 040° as 40°.",
        "Finds the third angle as 70°.",
        "Sets up sine rule with the 12 km side opposite the 40° angle.",
      ],
    },
  },

  {
    id: "5.15",
    title: "Plans & Elevations",
    slug: "plans-elevations",
    pageRef: "GCSE expansion",
    difficulty: "foundation",
    learn: {
      explanation:
        "Plans and elevations show a 3D object from different viewpoints: plan is from above, front elevation is from the front, and side elevation is from the side.",
      keyPoints: [
        "Plan view = looking directly down from above.",
        "Front elevation = looking from the front.",
        "Side elevation = looking from the side.",
        "Hidden edges may be shown using dashed lines.",
        "The three views together help define the 3D shape clearly.",
      ],
      workedExamples: [
        {
          problem: "Describe the plan view of a cube.",
          steps: [
            {
              step: 1,
              instruction: "Look from above.",
              working: "You see the top face.",
            },
            {
              step: 2,
              instruction: "Identify the shape.",
              working: "The top face of a cube is a square.",
            },
          ],
          answer: "Square",
        },
        {
          problem: "An upright cylinder is viewed from above. What is the plan view?",
          steps: [
            {
              step: 1,
              instruction: "Look down onto the top face.",
              working: "The top of an upright cylinder is circular.",
            },
          ],
          answer: "Circle",
        },
        {
          problem: "An upright cylinder is viewed from the front. What is the front elevation?",
          steps: [
            {
              step: 1,
              instruction: "Look from the side.",
              working: "The curved side appears as a rectangle.",
            },
          ],
          answer: "Rectangle",
        },
      ],
      whyItWorks:
        "A single drawing of a 3D object can be ambiguous. Orthographic views remove ambiguity by showing the shape from exact directions.",
    },
    practice: {
      warmUp: [
        {
          id: "5.15.w1",
          type: "choice",
          question: "What is the plan view of a sphere?",
          options: ["Circle", "Square", "Triangle"],
          answer: "Circle",
          hint1: "Look from above.",
          hint2: "A sphere appears round from every direction.",
          hint3: "Circle",
        },
        {
          id: "5.15.w2",
          type: "choice",
          question: "What is the front elevation of a cube?",
          options: ["Square", "Rectangle", "Triangle"],
          answer: "Square",
          hint1: "Look at the front face.",
          hint2: "The face of a cube is square.",
          hint3: "Square",
        },
      ],
      level1: [
        {
          id: "5.15.1",
          type: "choice",
          question: "What is the plan of a triangular prism lying on a rectangular face?",
          options: ["Triangle", "Rectangle", "Hexagon"],
          answer: "Rectangle",
          hint1: "The top face is long and rectangular.",
          hint2: "Look directly down.",
          hint3: "Rectangle",
        },
        {
          id: "5.15.2",
          type: "choice",
          question: "What is the side elevation of an upright cylinder?",
          options: ["Circle", "Rectangle", "Ellipse"],
          answer: "Rectangle",
          hint1: "View the cylinder from the side.",
          hint2: "It appears as a rectangle.",
          hint3: "Rectangle",
        },
      ],
      level2: [
        {
          id: "5.15.3",
          type: "choice",
          question: "What is the front elevation of a square-based pyramid with apex above the centre?",
          options: ["Triangle", "Square", "Pentagon"],
          answer: "Triangle",
          hint1: "Look from the front.",
          hint2: "The outline is triangular.",
          hint3: "Triangle",
        },
      ],
      level3: [
        {
          id: "5.15.4",
          type: "choice",
          question: "What is the plan of a square-based pyramid with apex above the centre?",
          options: ["Square only", "Square with diagonals to the centre", "Triangle"],
          answer: "Square with diagonals to the centre",
          hint1: "Top-down view shows the square base.",
          hint2: "The sloping edges meet at the centre.",
          hint3: "Square with diagonals to the centre.",
        },
      ],
    },
    aiChallenge: {
      prompt:
        "A solid is made from a cube of side 4 cm with a smaller cube of side 2 cm sitting on top in one corner. Describe the plan view, front elevation and side elevation.",
      successCriteria: [
        "Plan: 4 cm by 4 cm square with a 2 cm by 2 cm square shown in one corner.",
        "Front elevation: stepped/L-shaped outline showing total height 6 cm where the small cube sits.",
        "Side elevation: stepped/L-shaped outline, depending on the cube’s corner position.",
        "Mentions dashed lines where hidden edges are relevant.",
        "Uses the terms plan, front elevation and side elevation correctly.",
      ],
    },
  },

  {
    id: "5.16",
    title: "Circle Theorems",
    slug: "circle-theorems",
    pageRef: "Higher expansion",
    difficulty: "higher",
    learn: {
      explanation:
        "Circle theorems connect angles, chords, tangents and arcs. They are common in GCSE Higher proof and angle-finding questions.",
      keyPoints: [
        "Angle at the centre is twice the angle at the circumference standing on the same arc.",
        "Angle in a semicircle is 90°.",
        "Angles in the same segment are equal.",
        "Opposite angles in a cyclic quadrilateral add to 180°.",
        "A tangent is perpendicular to the radius at the point of contact.",
        "Tangents from the same external point are equal in length.",
        "Alternate segment theorem: angle between tangent and chord equals the angle in the alternate segment.",
        "The perpendicular from the centre to a chord bisects the chord.",
      ],
      workedExamples: [
        {
          problem: "The angle at the centre of a circle is 80°. Find the angle at the circumference standing on the same arc.",
          steps: [
            {
              step: 1,
              instruction: "Use the centre-circumference theorem.",
              working: "Angle at centre = 2 × angle at circumference.",
            },
            {
              step: 2,
              instruction: "Halve the centre angle.",
              working: "80 ÷ 2 = 40°",
            },
          ],
          answer: "40°",
        },
        {
          problem: "ABC is a triangle inside a circle where AC is a diameter. Find angle B.",
          steps: [
            {
              step: 1,
              instruction: "Use the semicircle theorem.",
              working: "The angle in a semicircle is 90°.",
            },
          ],
          answer: "90°",
        },
        {
          problem: "ABCD is a cyclic quadrilateral with angle A = 110°. Find opposite angle C.",
          steps: [
            {
              step: 1,
              instruction: "Use cyclic quadrilateral rule.",
              working: "Opposite angles add to 180°.",
            },
            {
              step: 2,
              instruction: "Subtract.",
              working: "180 − 110 = 70°",
            },
          ],
          answer: "70°",
        },
      ],
      whyItWorks:
        "Most circle theorems come from the symmetry of a circle and the relationship between centre angles and circumference angles.",
    },
    practice: {
      warmUp: [
        {
          id: "5.16.w1",
          type: "number",
          question: "Angle at the centre is 100°. What is the angle at the circumference on the same arc?",
          answer: "50",
          hint1: "The angle at the circumference is half the centre angle.",
          hint2: "100 ÷ 2.",
          hint3: "50°",
        },
        {
          id: "5.16.w2",
          type: "number",
          question: "What is the angle in a semicircle?",
          answer: "90",
          hint1: "Use Thales' theorem.",
          hint2: "Angle in a semicircle is a right angle.",
          hint3: "90°",
        },
      ],
      level1: [
        {
          id: "5.16.1",
          type: "number",
          question: "A cyclic quadrilateral has one angle of 75°. Find the opposite angle.",
          answer: "105",
          hint1: "Opposite angles in a cyclic quadrilateral add to 180°.",
          hint2: "180 − 75.",
          hint3: "105°",
        },
        {
          id: "5.16.2",
          type: "number",
          question: "What is the angle between a tangent and a radius at the point of contact?",
          answer: "90",
          hint1: "A tangent is perpendicular to the radius.",
          hint2: "Perpendicular means 90°.",
          hint3: "90°",
        },
      ],
      level2: [
        {
          id: "5.16.3",
          type: "number",
          question: "An angle at the centre is 140°. The angle at the circumference on the same arc is what?",
          answer: "70",
          hint1: "Halve the centre angle.",
          hint2: "140 ÷ 2.",
          hint3: "70°",
        },
        {
          id: "5.16.4",
          type: "number",
          question: "Two tangents from external point P touch a circle at A and B. PA = 8 cm. Find PB.",
          answer: "8",
          hint1: "Tangents from the same external point are equal.",
          hint2: "PA = PB.",
          hint3: "8 cm",
        },
      ],
      level3: [
        {
          id: "5.16.5",
          type: "number",
          question: "Using the alternate segment theorem, if the tangent-chord angle is 35°, what is the angle in the alternate segment?",
          answer: "35",
          hint1: "The alternate segment theorem gives equal angles.",
          hint2: "So the matching angle is also 35°.",
          hint3: "35°",
        },
      ],
    },
    aiChallenge: {
      prompt:
        "Prove that the angle subtended by a diameter at the circumference is always 90°. Use the centre-circumference angle theorem.",
      successCriteria: [
        "Sets up a circle with AB as the diameter and centre O on AB.",
        "States that the angle at the centre standing on diameter AB is 180°.",
        "Uses angle at centre = 2 × angle at circumference.",
        "Concludes 180° = 2 × angle ACB.",
        "Therefore angle ACB = 90°.",
      ],
    },
  },
];

export default geometryTopics;